/**
 * Playback control.
 *
 * SIMULATED. Owns the interval that advances `elapsed` until it reaches the playing
 * song's parsed length, then stops. There is no audio element; see
 * src/stores/playerStore.ts.
 *
 * The interval lives here rather than in the store because it is a subscription
 * with a lifecycle, and effects belong in the React layer. Only one component
 * should call `useSimulatedPlayback()` — the app shell does — or the clock would
 * tick once per caller.
 */
import { useEffect, useRef } from 'react';
import { formatTime, parseLength } from '../lib/formatting';
import { persistSongLength } from '../lib/songRuntime';
import { tickIntervalMs } from '../lib/playback';
import {
  attachAudioRecovery,
  audioPlaybackPosition,
  playDeliveredAudio,
  prepareAudioPlayback,
  stableAudioSource,
} from '../lib/audioDelivery';
import { usePlayerStore } from '../stores/playerStore';
import { selectSongById, useSongsStore } from '../stores/songsStore';
import { useUiStore } from '../stores/uiStore';
import type { Song, SongVersion } from '../types/domain';

let sharedAudio: HTMLAudioElement | null = null;
const preloadAudios = new Map<string, HTMLAudioElement>();
let loadedSongId: string | null = null;
let loadedVersionId: string | null = null;
let loadedSourceUrl: string | null = null;
let previewObjectUrl: string | null = null;
let previewEndedHandler: (() => void) | null = null;
let previewRestoreSnapshot: { src: string; elapsed: number; wasPaused: boolean } | null = null;
let backgroundVersionSourceUrl: string | null = null;
let backgroundVersionEndedHandler: (() => void) | null = null;
let backgroundVersionRestoreSnapshot: {
  songId: string | null;
  versionId: string | null;
  src: string;
  elapsed: number;
  wasPaused: boolean;
} | null = null;
let pendingExplicitSeek: { songId: string; versionId: string | null } | null = null;
const knownSongs = new Map<string, Song>();
const recentSongIds: string[] = [];
const PRELOAD_RECENT_LIMIT = 3;
const START_BUFFER_FRACTION = 0.1;
const START_BUFFER_MIN_SEC = 2;
const START_BUFFER_MAX_SEC = 12;

function getSharedAudio(): HTMLAudioElement | null {
  if (typeof window === 'undefined' || typeof Audio === 'undefined') return null;
  if (!sharedAudio) {
    sharedAudio = new Audio();
    sharedAudio.preload = 'auto';
    attachAudioRecovery(sharedAudio, () => {
      const state = usePlayerStore.getState();
      return backgroundVersionSourceUrl ? !state.backgroundVersionPaused : !state.paused;
    }, () => {
      usePlayerStore.setState({ loading: false, pendingStart: false, paused: true });
      useUiStore.getState().showToast('Playback unavailable. Please try again.');
    });
    (window as Window & { __LyrixaAudio?: HTMLAudioElement }).__LyrixaAudio = sharedAudio;
  }
  return sharedAudio;
}

function getPreloadAudio(songId: string): HTMLAudioElement | null {
  if (typeof window === 'undefined' || typeof Audio === 'undefined') return null;
  const existing = preloadAudios.get(songId);
  if (existing) return existing;
  const created = new Audio();
  created.preload = 'metadata';
  preloadAudios.set(songId, created);
  return created;
}

function resolvedAudioUrl(url: string): string {
  if (typeof window === 'undefined') return url;
  return /^https?:\/\//.test(url) ? url : `${window.location.origin}${url}`;
}

function rememberSongs(songs: readonly Song[]): void {
  for (const song of songs) knownSongs.set(song.id, song);
}

function shouldPersistLoadedDuration(song: Song, durationSec: number): boolean {
  if (!song.audioUrl) return false;
  if (!Number.isFinite(durationSec) || durationSec <= 0) return false;
  const declared = parseLength(song.length);
  return declared <= 0 || Math.abs(declared - durationSec) >= 1;
}

const persistedLoadedDuration = new Map<string, string>();

function startupBufferTarget(durationSec: number): number {
  if (!Number.isFinite(durationSec) || durationSec <= 0) return START_BUFFER_MIN_SEC;
  return Math.min(START_BUFFER_MAX_SEC, Math.max(START_BUFFER_MIN_SEC, durationSec * START_BUFFER_FRACTION));
}

function bufferedAhead(audio: HTMLAudioElement): number {
  const current = audio.currentTime;
  for (let index = 0; index < audio.buffered.length; index += 1) {
    const start = audio.buffered.start(index);
    const end = audio.buffered.end(index);
    if (current >= start && current <= end) return end - current;
  }
  return 0;
}

function noteRecentSong(songId: string): void {
  const at = recentSongIds.indexOf(songId);
  if (at >= 0) recentSongIds.splice(at, 1);
  recentSongIds.unshift(songId);
  if (recentSongIds.length > PRELOAD_RECENT_LIMIT) recentSongIds.length = PRELOAD_RECENT_LIMIT;
}

function warmSongForPlayback(song: Song | null): void {
  if (!song?.audioUrl) return;
  const audio = getPreloadAudio(song.id);
  if (!audio) return;
  const sourceUrl = resolvedAudioUrl(song.audioUrl);
  if (audio.src === sourceUrl) return;
  audio.src = sourceUrl;
  audio.load();
}

function warmRecentSongs(songs: readonly Song[]): void {
  for (const songId of recentSongIds) warmSongForPlayback(rememberedSong(songId, songs));
}

function isPreviewSource(audio: HTMLAudioElement): boolean {
  return Boolean(previewObjectUrl && audio.src === previewObjectUrl);
}

function detachPreviewEndedHandler(audio: HTMLAudioElement): void {
  if (!previewEndedHandler) return;
  audio.removeEventListener('ended', previewEndedHandler);
  previewEndedHandler = null;
}

function clearPreviewObjectUrl(): void {
  if (!previewObjectUrl) return;
  URL.revokeObjectURL(previewObjectUrl);
  previewObjectUrl = null;
}

function activePreviewAudio(): HTMLAudioElement | null {
  return sharedAudio && previewObjectUrl && isPreviewSource(sharedAudio) ? sharedAudio : null;
}

function isBackgroundVersionSource(audio: HTMLAudioElement): boolean {
  return Boolean(backgroundVersionSourceUrl && stableAudioSource(audio) === backgroundVersionSourceUrl);
}

function wantsPlayback(audio: HTMLAudioElement): boolean {
  const state = usePlayerStore.getState();
  if (isPreviewSource(audio)) return !state.previewPaused;
  if (isBackgroundVersionSource(audio)) return !state.backgroundVersionPaused;
  return !state.paused;
}

function detachBackgroundVersionEndedHandler(audio: HTMLAudioElement): void {
  if (!backgroundVersionEndedHandler) return;
  audio.removeEventListener('ended', backgroundVersionEndedHandler);
  backgroundVersionEndedHandler = null;
}

function clearBackgroundVersionSource(): void {
  backgroundVersionSourceUrl = null;
}

function clearLoadedPlaybackIdentity(): void {
  loadedSongId = null;
  loadedVersionId = null;
  loadedSourceUrl = null;
}

function discardBackgroundVersionPlayback(audio: HTMLAudioElement): void {
  detachBackgroundVersionEndedHandler(audio);
  backgroundVersionRestoreSnapshot = null;
  clearBackgroundVersionSource();
  usePlayerStore.getState().clearBackgroundVersion();
  clearLoadedPlaybackIdentity();
  audio.pause();
  audio.removeAttribute('src');
  audio.load();
}

function handBackAudioFromBackgroundVersion(targetSongId: string | null, targetVersionId: string | null): void {
  const audio = sharedAudio;
  if (!audio || !backgroundVersionSourceUrl) return;
  const snapshot = backgroundVersionRestoreSnapshot;
  if (snapshot?.songId === targetSongId && (snapshot.versionId ?? null) === (targetVersionId ?? null)) {
    void restorePlaybackAfterBackgroundVersion(audio);
    return;
  }
  discardBackgroundVersionPlayback(audio);
}

function hasBackgroundVersionPlayback(): boolean {
  return Boolean(backgroundVersionSourceUrl);
}

async function restorePlaybackAfterBackgroundVersion(audio: HTMLAudioElement): Promise<void> {
  detachBackgroundVersionEndedHandler(audio);
  const snapshot = backgroundVersionRestoreSnapshot;
  backgroundVersionRestoreSnapshot = null;
  clearBackgroundVersionSource();
  usePlayerStore.getState().clearBackgroundVersion();

  if (!snapshot?.src) {
    loadedSongId = null;
    loadedVersionId = null;
    loadedSourceUrl = null;
    audio.pause();
    audio.removeAttribute('src');
    audio.load();
    return;
  }

  const resumeElapsed = usePlayerStore.getState().elapsed;
  loadedSongId = snapshot.songId;
  loadedVersionId = snapshot.versionId;
  loadedSourceUrl = snapshot.src;
  audio.pause();
  audio.src = snapshot.src;
  audio.currentTime = Number.isFinite(resumeElapsed) && resumeElapsed >= 0 ? resumeElapsed : snapshot.elapsed;
  audio.load();

  if (!snapshot.wasPaused) {
    try {
      await playDeliveredAudio(audio, () => wantsPlayback(audio));
    } catch (error) {
      console.error('[player] background version restore failed', error);
    }
  }
}

async function restorePlaybackAfterPreview(audio: HTMLAudioElement): Promise<void> {
  detachPreviewEndedHandler(audio);
  const snapshot = previewRestoreSnapshot;
  previewRestoreSnapshot = null;
  clearPreviewObjectUrl();
  usePlayerStore.getState().clearPreview();

  if (!snapshot?.src) {
    loadedSongId = null;
    loadedVersionId = null;
    loadedSourceUrl = null;
    audio.pause();
    audio.removeAttribute('src');
    audio.load();
    return;
  }

  const resumeElapsed = usePlayerStore.getState().elapsed;
  audio.pause();
  audio.src = snapshot.src;
  audio.currentTime = Number.isFinite(resumeElapsed) && resumeElapsed >= 0 ? resumeElapsed : snapshot.elapsed;
  audio.load();

  if (!snapshot.wasPaused) {
    try {
      await playDeliveredAudio(audio, () => wantsPlayback(audio));
    } catch (error) {
      console.error('[player] preview restore failed', error);
    }
  }
}

export async function playPreviewAudio(
  songId: string,
  blob: Blob,
  options: { startAt?: number; paused?: boolean } = {}
): Promise<void> {
  const audio = getSharedAudio();
  if (!audio) return;

  if (isBackgroundVersionSource(audio) || usePlayerStore.getState().backgroundVersionId) {
    await restorePlaybackAfterBackgroundVersion(audio);
  }

  const replacingPreview = isPreviewSource(audio) && usePlayerStore.getState().previewSongId === songId;
  const resumeElapsed = Math.max(
    0,
    options.startAt ?? (replacingPreview ? audio.currentTime : usePlayerStore.getState().previewElapsed)
  );
  const resumePaused = options.paused ?? (replacingPreview ? audio.paused : usePlayerStore.getState().previewPaused);

  if (!previewRestoreSnapshot) {
    previewRestoreSnapshot = {
      src: stableAudioSource(audio),
      elapsed: audio.currentTime,
      wasPaused: usePlayerStore.getState().paused || audio.paused,
    };
  }

  detachPreviewEndedHandler(audio);
  clearPreviewObjectUrl();

  previewObjectUrl = URL.createObjectURL(blob);
  const previewSrc = previewObjectUrl;
  previewEndedHandler = () => {
    if (audio.src !== previewSrc) return;
    void restorePlaybackAfterPreview(audio);
  };

  usePlayerStore.getState().startPreview(songId);
  usePlayerStore.getState().setPreviewElapsed(resumeElapsed);
  audio.addEventListener('ended', previewEndedHandler, { once: true });
  audio.pause();
  audio.src = previewSrc;
  audio.currentTime = resumeElapsed;
  audio.load();
  if (resumePaused) {
    usePlayerStore.getState().setPreviewPaused(true);
    return;
  }
  await audio.play();
  usePlayerStore.getState().setPreviewPaused(false);
}

export function togglePreviewAudio(): void {
  const audio = activePreviewAudio();
  const { previewSongId, setPreviewPaused } = usePlayerStore.getState();
  if (!audio || !previewSongId) return;
  if (audio.paused) {
    void audio.play().then(() => setPreviewPaused(false)).catch((error) => {
      console.error('[player] preview play failed', error);
    });
    return;
  }
  audio.pause();
  setPreviewPaused(true);
}

export function seekPreviewAudio(seconds: number): void {
  const audio = activePreviewAudio();
  const { previewSongId, setPreviewElapsed } = usePlayerStore.getState();
  if (!audio || !previewSongId) return;
  const next = Number.isFinite(audio.duration) && audio.duration > 0 ? Math.max(0, Math.min(audio.duration, seconds)) : Math.max(0, seconds);
  audio.currentTime = next;
  setPreviewElapsed(next);
}

export function pausePreviewAudio(): void {
  const audio = activePreviewAudio();
  const { previewSongId, setPreviewElapsed, setPreviewPaused } = usePlayerStore.getState();
  if (!audio || !previewSongId || audio.paused) return;
  setPreviewElapsed(audio.currentTime);
  audio.pause();
  setPreviewPaused(true);
}

export function suspendPreviewAudio(): void {
  const audio = activePreviewAudio();
  const { previewSongId, elapsed, setPreviewElapsed, setPreviewPaused } = usePlayerStore.getState();
  if (!audio || !previewSongId) return;
  setPreviewElapsed(audio.currentTime);
  audio.pause();
  setPreviewPaused(true);

  const snapshot = previewRestoreSnapshot;
  if (!snapshot?.src) {
    loadedSongId = null;
    loadedVersionId = null;
    loadedSourceUrl = null;
    audio.removeAttribute('src');
    audio.load();
    return;
  }

  audio.src = snapshot.src;
  audio.currentTime = Number.isFinite(elapsed) && elapsed >= 0 ? elapsed : snapshot.elapsed;
  audio.load();
}

export function playPreviewAudioFromCurrentPosition(): void {
  const audio = getSharedAudio();
  const { previewSongId, previewElapsed, setPreviewPaused } = usePlayerStore.getState();
  if (!audio || !previewSongId || !previewObjectUrl) return;
  if (!isPreviewSource(audio)) {
    detachPreviewEndedHandler(audio);
    const previewSrc = previewObjectUrl;
    previewEndedHandler = () => {
      if (audio.src !== previewSrc) return;
      void restorePlaybackAfterPreview(audio);
    };
    audio.addEventListener('ended', previewEndedHandler, { once: true });
    audio.pause();
    audio.src = previewSrc;
    audio.currentTime = Math.max(0, previewElapsed);
    audio.load();
  }
  if (!audio.paused) return;
  void audio.play().then(() => setPreviewPaused(false)).catch((error) => {
    console.error('[player] preview play failed', error);
  });
}

export function stopPreviewAudio(): void {
  const audio = sharedAudio;
  if (!audio || !previewObjectUrl) return;
  void restorePlaybackAfterPreview(audio);
}

export async function playBackgroundVersionAudio(songId: string, versionId: string): Promise<void> {
  const audio = getSharedAudio();
  if (!audio) return;
  const song = rememberedSong(songId, useSongsStore.getState().songs);
  const version = song?.versions.find((candidate) => candidate.id === versionId) ?? null;
  const sourceAudioUrl = playbackUrlFor(song, versionId);
  if (!song || !version || !sourceAudioUrl) return;

  if (isPreviewSource(audio) || usePlayerStore.getState().previewSongId) {
    await restorePlaybackAfterPreview(audio);
  }

  if (!backgroundVersionRestoreSnapshot) {
    const { songId: activeSongId, versionId: activeVersionId, paused } = usePlayerStore.getState();
    backgroundVersionRestoreSnapshot = {
      songId: activeSongId,
      versionId: activeVersionId,
      src: stableAudioSource(audio),
      elapsed: audio.currentTime,
      wasPaused: paused || audio.paused,
    };
  }

  detachBackgroundVersionEndedHandler(audio);
  clearBackgroundVersionSource();

  const sourceUrl = resolvedAudioUrl(sourceAudioUrl);
  backgroundVersionSourceUrl = sourceUrl;
  backgroundVersionEndedHandler = () => {
    if (stableAudioSource(audio) !== sourceUrl) return;
    void restorePlaybackAfterBackgroundVersion(audio);
  };

  usePlayerStore.getState().setBackgroundVersion(songId, versionId);
  audio.addEventListener('ended', backgroundVersionEndedHandler, { once: true });
  audio.pause();
  audio.src = sourceUrl;
  audio.currentTime = 0;
  audio.load();
  await playDeliveredAudio(audio, () => wantsPlayback(audio));
}

export function toggleBackgroundVersionAudio(songId: string, versionId: string): void {
  const audio = sharedAudio;
  const {
    backgroundVersionSongId,
    backgroundVersionId,
    backgroundVersionPaused,
    setBackgroundVersionPaused,
  } = usePlayerStore.getState();
  const sameVersion =
    audio &&
    backgroundVersionSongId === songId &&
    backgroundVersionId === versionId &&
    isBackgroundVersionSource(audio);

  if (!sameVersion) {
    void playBackgroundVersionAudio(songId, versionId);
    return;
  }

  if (backgroundVersionPaused) {
    setBackgroundVersionPaused(false);
    void playDeliveredAudio(audio, () => wantsPlayback(audio)).catch((error) => {
      console.error('[player] background version play failed', error);
    });
    return;
  }

  audio.pause();
  setBackgroundVersionPaused(true);
}

export function stopBackgroundVersionAudio(): void {
  const audio = sharedAudio;
  if (!audio || !backgroundVersionSourceUrl) return;
  void restorePlaybackAfterBackgroundVersion(audio);
}

export function activeAudioForPlayback(): HTMLAudioElement | null {
  return getSharedAudio();
}

export function bufferedSecondsForPlayback(): number {
  const audio = getSharedAudio();
  if (!audio) return 0;
  let buffered = 0;
  for (let index = 0; index < audio.buffered.length; index += 1) {
    buffered = Math.max(buffered, audio.buffered.end(index));
  }
  return buffered;
}

export function rememberSongsForPlayback(songs: readonly Song[]): void {
  rememberSongs(songs);
  warmRecentSongs(songs);
}

export function warmGeneratedSongForPlayback(song: Song): void {
  rememberSongs([song]);
  warmSongForPlayback(song);
}

export function resetRememberedSongsForPlayback(): void {
  knownSongs.clear();
  recentSongIds.length = 0;
  const shared = sharedAudio;
  if (shared) {
    detachPreviewEndedHandler(shared);
    detachBackgroundVersionEndedHandler(shared);
    previewRestoreSnapshot = null;
    backgroundVersionRestoreSnapshot = null;
    clearPreviewObjectUrl();
    clearBackgroundVersionSource();
    shared.pause();
    shared.removeAttribute('src');
    shared.load();
  }
  sharedAudio = null;
  loadedSongId = null;
  loadedVersionId = null;
  loadedSourceUrl = null;
  for (const audio of preloadAudios.values()) {
    audio.pause();
    audio.removeAttribute('src');
    audio.load();
  }
  preloadAudios.clear();
}

function rememberedSong(songId: string | null, songs: readonly Song[]): Song | null {
  const fromLibrary = selectSongById(songs as Song[], songId);
  if (fromLibrary) {
    knownSongs.set(fromLibrary.id, fromLibrary);
    return fromLibrary;
  }
  return songId ? knownSongs.get(songId) ?? null : null;
}

function loadedVersionOf(song: Song | null, versionId: string | null): SongVersion | null {
  return song && versionId ? song.versions.find((version) => version.id === versionId) ?? null : null;
}

function playbackUrlFor(song: Song | null, versionId: string | null): string | null {
  const loadedVersion = loadedVersionOf(song, versionId);
  if (!loadedVersion) return song?.audioUrl ?? null;
  if (loadedVersion.audioUrl) return loadedVersion.audioUrl;
  // Legacy rows can be missing the stored version URL even though the backing version asset
  // exists. Reconstruct it from the parent song only when the song itself has audio, so mock
  // seed songs without real media stay on the simulated path instead of inventing a dead URL.
  return song?.audioUrl ? `/api/media/audio/${song.id}/versions/${loadedVersion.id}` : null;
}

/**
 * Drives the simulated playback clock. Call exactly once, high in the tree.
 *
 * On reaching the song's length: repeats from zero if `repeat` is on, otherwise
 * advances to the next song in the queue, otherwise stops. The prototype ignored
 * `repeat` here, so a track ended regardless of the toggle's state — the control is
 * honoured now that it flows through one place.
 *
 * `repeat` is checked BEFORE the queue, which keeps its existing meaning: it loops
 * the track you are on. Reading it as "loop the playlist" would silently change what
 * the button does to anyone using it on a single song, which is every use of it up to
 * now. Advancing through a queue is what makes a playlist play as a playlist, and it
 * happens here rather than in a second timer for the same reason there is one clock
 * at all.
 *
 * PAUSING TEARS THE INTERVAL DOWN, rather than being tested inside the callback. The
 * two behave identically for a paused player and differ on resume: a timer left
 * running for a minute while paused fires the moment it is unpaused and lands the
 * first tick a fraction of a second later. Restarting the interval makes the first
 * second after resume a whole second, which is what the readout claims it is.
 *
 * SPEED IS THE TICK RATE, and this is the whole of what the speed selector does. The
 * interval is `1000 / speed` ms, so one *song*-second passes in half a wall-clock second
 * at 2× and in two at 0.5×: a 3:12 track genuinely finishes in 1:36 at double speed, the
 * clock counts through it at double rate, and the seek fill and timeline playhead — both
 * rendered from `elapsed` — cross at double rate with it. `speed` was recorded and read
 * by nothing before this, which made the control a label. See src/lib/playback.ts for
 * why the arithmetic is a separate module and for what this still cannot claim: there is
 * no audio, so there is no pitch and no resampling, only a rate.
 *
 * Changing speed mid-track therefore restarts the interval and keeps `elapsed` — the
 * position is state in the store, not something the timer owns — so switching to 2× at
 * 1:20 carries on from 1:20, faster. Changing it while paused starts no timer at all and
 * the new rate applies on resume, because the interval is created by this same effect.
 */
export function useSimulatedPlayback(): void {
  const songId = usePlayerStore((s) => s.songId);
  const versionId = usePlayerStore((s) => s.versionId);
  const paused = usePlayerStore((s) => s.paused);
  const backgroundVersionPaused = usePlayerStore((s) => s.backgroundVersionPaused);
  const repeat = usePlayerStore((s) => s.repeat);
  const speed = usePlayerStore((s) => s.speed);
  const volume = usePlayerStore((s) => s.volume);
  const muted = usePlayerStore((s) => s.muted);
  const elapsed = usePlayerStore((s) => s.elapsed);
  const setMediaDurationSec = usePlayerStore((s) => s.setMediaDurationSec);
  const setBufferedSec = usePlayerStore((s) => s.setBufferedSec);
  const setLoading = usePlayerStore((s) => s.setLoading);
  const setPendingStart = usePlayerStore((s) => s.setPendingStart);
  const songs = useSongsStore((s) => s.songs);
  const updateSong = useSongsStore((s) => s.updateSong);
  const recordPlay = useSongsStore((s) => s.recordPlay);
  const playProgressRef = useRef<{
    songId: string | null;
    versionId: string | null;
    counted: boolean;
    listenedSec: number;
    lastElapsed: number;
  }>({ songId: null, versionId: null, counted: false, listenedSec: 0, lastElapsed: 0 });

  useEffect(() => {
    rememberSongs(songs);
  }, [songs]);

  useEffect(() => {
    const audio = getSharedAudio();
    if (!audio) return;

    const handleTime = () => {
      if (isPreviewSource(audio)) {
        usePlayerStore.getState().setPreviewElapsed(audio.currentTime);
        return;
      }
      if (isBackgroundVersionSource(audio)) return;
      usePlayerStore.getState().setElapsed(audioPlaybackPosition(audio));
      usePlayerStore.getState().setBufferedSec(bufferedAhead(audio) + audio.currentTime);
    };
    const handleMetadata = () => {
      const duration = Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : null;
      if (isPreviewSource(audio)) {
        usePlayerStore.getState().setPreviewDurationSec(duration);
        return;
      }
      if (isBackgroundVersionSource(audio)) return;
      setMediaDurationSec(duration);
      const { songId: activeSongId, versionId: activeVersionId } = usePlayerStore.getState();
      const currentSong = activeSongId ? rememberedSong(activeSongId, useSongsStore.getState().songs) : null;
      // A loaded version has its own declared runtime already; persisting the audio element's
      // duration back onto the parent song here would overwrite the HEAD length with a
      // version-only value, which is exactly the mismatch Version History is meant to avoid.
      if (activeVersionId || !currentSong || !duration || !shouldPersistLoadedDuration(currentSong, duration)) return;
      const nextLength = formatTime(Math.round(duration));
      if (persistedLoadedDuration.get(currentSong.id) === nextLength) return;
      persistedLoadedDuration.set(currentSong.id, nextLength);
      persistSongLength(currentSong.id, nextLength);
      updateSong(currentSong.id, { length: nextLength });
    };
    const handleLoadStart = () => {
      if (isPreviewSource(audio) || isBackgroundVersionSource(audio)) return;
      setBufferedSec(0);
      setLoading(true);
    };
    const handleProgress = () => {
      if (isPreviewSource(audio) || isBackgroundVersionSource(audio)) return;
      const bufferedSec = bufferedAhead(audio) + audio.currentTime;
      setBufferedSec(bufferedSec);
      if (bufferedAhead(audio) >= startupBufferTarget(audio.duration)) {
        setLoading(false);
        if (usePlayerStore.getState().pendingStart) {
          setPendingStart(false);
          void playDeliveredAudio(audio, () => wantsPlayback(audio)).catch((error) => {
            setLoading(false);
            console.error('[player] audio.play failed', error);
          });
        }
      }
    };
    const handleReady = () => {
      if (isPreviewSource(audio)) {
        usePlayerStore.getState().setPreviewPaused(audio.paused);
        return;
      }
      if (isBackgroundVersionSource(audio)) {
        usePlayerStore.getState().setBackgroundVersionPaused(audio.paused);
        return;
      }
      if (bufferedAhead(audio) >= startupBufferTarget(audio.duration)) {
        setLoading(false);
        if (usePlayerStore.getState().pendingStart) {
          setPendingStart(false);
          void playDeliveredAudio(audio, () => wantsPlayback(audio)).catch((error) => {
            setLoading(false);
            console.error('[player] audio.play failed', error);
          });
        }
      }
    };
    const handleWaiting = () => {
      if (isPreviewSource(audio) || isBackgroundVersionSource(audio)) return;
      setLoading(true);
    };
    const handleEnded = () => {
      if (isPreviewSource(audio) || isBackgroundVersionSource(audio)) return;
      const { repeat, queue, songId, versionId, playNext, stop } = usePlayerStore.getState();
      if (repeat) {
        audio.currentTime = 0;
        void playDeliveredAudio(audio, () => wantsPlayback(audio)).catch((error) => {
          console.error('[player] audio.play failed', error);
        });
        return;
      }
      if (songId && queue[queue.indexOf(songId) + 1]) {
        playNext();
        return;
      }
      if (versionId) {
        const finishedAt = Math.round(
          Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : audio.currentTime
        );
        usePlayerStore.setState({
          paused: true,
          pendingStart: false,
          loading: false,
          elapsed: finishedAt,
          bufferedSec: finishedAt,
        });
        return;
      }
      stop();
    };

    audio.addEventListener('timeupdate', handleTime);
    audio.addEventListener('loadedmetadata', handleMetadata);
    audio.addEventListener('durationchange', handleMetadata);
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('progress', handleProgress);
    audio.addEventListener('canplay', handleReady);
    audio.addEventListener('playing', handleReady);
    audio.addEventListener('waiting', handleWaiting);
    audio.addEventListener('ended', handleEnded);
    return () => {
      audio.removeEventListener('timeupdate', handleTime);
      audio.removeEventListener('loadedmetadata', handleMetadata);
      audio.removeEventListener('durationchange', handleMetadata);
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('progress', handleProgress);
      audio.removeEventListener('canplay', handleReady);
      audio.removeEventListener('playing', handleReady);
      audio.removeEventListener('waiting', handleWaiting);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  useEffect(() => {
    const audio = getSharedAudio();
    const song = rememberedSong(songId, songs);
    const sourceAudioUrl = playbackUrlFor(song, versionId);
    if (!audio) return;

    if (song && sourceAudioUrl) {
      const sourceUrl = resolvedAudioUrl(sourceAudioUrl);
      if (loadedSongId !== song.id || loadedVersionId !== (versionId ?? null) || loadedSourceUrl !== sourceUrl) {
        handBackAudioFromBackgroundVersion(song.id, versionId ?? null);
        if (previewObjectUrl) stopPreviewAudio();
        loadedSongId = song.id;
        loadedVersionId = versionId ?? null;
        loadedSourceUrl = sourceUrl;
        noteRecentSong(song.id);
        setMediaDurationSec(null);
        setBufferedSec(0);
        setLoading(true);
        audio.src = sourceUrl;
        audio.currentTime = 0;
        audio.load();
        void prepareAudioPlayback(audio).catch(() => {
          if (loadedSourceUrl !== sourceUrl) return;
          usePlayerStore.setState({ loading: false, pendingStart: false, paused: true });
          useUiStore.getState().showToast('Playback unavailable. Please try again.');
        });
      }
      return;
    }

    handBackAudioFromBackgroundVersion(null, null);
    audio.pause();
    setLoading(false);
    setPendingStart(false);
    setBufferedSec(0);
    if (!songId) {
      setMediaDurationSec(null);
      audio.removeAttribute('src');
      audio.load();
      clearLoadedPlaybackIdentity();
    }
  }, [songId, versionId, songs]);

  useEffect(() => {
    const currentSongId = songId ?? null;
    const currentVersionId = versionId ?? null;
    const previous = playProgressRef.current;
    const changedTrack = previous.songId !== currentSongId || previous.versionId !== currentVersionId;
    const restartedFromZero = currentSongId !== null && !paused && elapsed === 0 && previous.lastElapsed > 0;

    if (changedTrack || restartedFromZero) {
      playProgressRef.current = {
        songId: currentSongId,
        versionId: currentVersionId,
        counted: false,
        listenedSec: 0,
        lastElapsed: elapsed,
      };
    }

    const seekingThisRun =
      pendingExplicitSeek?.songId === currentSongId && pendingExplicitSeek.versionId === currentVersionId;

    if (!paused && currentSongId && elapsed > playProgressRef.current.lastElapsed) {
      if (seekingThisRun) pendingExplicitSeek = null;
      else playProgressRef.current.listenedSec += elapsed - playProgressRef.current.lastElapsed;
    } else if (seekingThisRun && elapsed !== playProgressRef.current.lastElapsed) {
      pendingExplicitSeek = null;
    }

    if (currentSongId && !paused && !playProgressRef.current.counted && playProgressRef.current.listenedSec >= 30) {
      recordPlay(currentSongId);
      playProgressRef.current.counted = true;
    }

    playProgressRef.current.lastElapsed = elapsed;
  }, [songId, versionId, paused, elapsed, recordPlay]);

  useEffect(() => {
    const audio = getSharedAudio();
    const song = rememberedSong(songId, songs);
    const sourceAudioUrl = playbackUrlFor(song, versionId);
    if (!audio || !sourceAudioUrl) return;

    audio.playbackRate = speed;
    audio.volume = Math.max(0, Math.min(1, volume / 100));
    audio.muted = muted;
    if (isPreviewSource(audio)) return;
    if (isBackgroundVersionSource(audio)) {
      if (backgroundVersionPaused) audio.pause();
      else if (audio.paused) {
        void playDeliveredAudio(audio, () => wantsPlayback(audio)).catch((error) => {
          console.error('[player] background version play failed', error);
        });
      }
      return;
    }

    if (paused) {
      setPendingStart(false);
      setLoading(false);
      audio.pause();
    } else {
      if (audio.paused) {
        void prepareAudioPlayback(audio).catch(() => {
          if (loadedSourceUrl !== resolvedAudioUrl(sourceAudioUrl)) return;
          usePlayerStore.setState({ paused: true, loading: false, pendingStart: false });
          useUiStore.getState().showToast('Playback unavailable. Please try again.');
        });
      }
      const ready = bufferedAhead(audio) >= startupBufferTarget(audio.duration);
      if (!ready) {
        setPendingStart(true);
        setLoading(true);
      } else if (audio.paused) {
        setPendingStart(false);
        void playDeliveredAudio(audio, () => wantsPlayback(audio)).catch((error) => {
          setLoading(false);
          console.error('[player] audio.play failed', error);
        });
      }
    }
  }, [songId, versionId, songs, paused, backgroundVersionPaused, speed, volume, muted]);

  useEffect(() => {
    const audio = getSharedAudio();
    const song = rememberedSong(songId, songs);
    const sourceAudioUrl = playbackUrlFor(song, versionId);
    if (!audio || !sourceAudioUrl) return;
    if (isPreviewSource(audio) || isBackgroundVersionSource(audio)) return;
    if (Math.abs(audio.currentTime - elapsed) > 0.5) {
      audio.currentTime = Math.max(0, elapsed);
    }
  }, [songId, versionId, songs, elapsed]);

  useEffect(() => {
    if (!songId || paused) return;
    const song = rememberedSong(songId, songs);
    if (!song) return;
    if (song.audioUrl) return;

    // The loaded version's own runtime, not the song's: a version created by a Trim is
    // shorter than the track it came from, and playing it must end where IT ends. Falls
    // back to the song length when no version is loaded, which is the common case.
    const loadedVersion = versionId ? song.versions.find((v) => v.id === versionId) : null;
    const durationSec = parseLength(loadedVersion?.length ?? song.length);
    const interval = setInterval(() => {
      const { elapsed, setElapsed, stop, queue, playNext } = usePlayerStore.getState();
      const nextElapsed = elapsed + 1;
      if (nextElapsed >= durationSec) {
        if (repeat) {
          playProgressRef.current = { songId, versionId: versionId ?? null, counted: false, listenedSec: 0, lastElapsed: 0 };
          setElapsed(0);
        } else if (queue[queue.indexOf(songId) + 1]) playNext();
        else if (versionId) {
          usePlayerStore.setState({
            paused: true,
            pendingStart: false,
            loading: false,
            elapsed: durationSec,
            bufferedSec: durationSec,
          });
        } else stop();
        return;
      }
      setElapsed(nextElapsed);
    }, tickIntervalMs(speed));

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [songId, versionId, songs, paused, repeat, speed, recordPlay]);
}

/**
 * Read/act interface for the player, for use by the PlayerBar and song cards.
 *
 * TWO PREDICATES, NOT ONE, and the difference is what a pause looks like.
 *
 * `isActive(id)` asks whether the store's `elapsed` describes this song. `isPlaying(id)`
 * asks whether it is advancing. Every surface needs both, for different things:
 *
 *   - the play button's label and the waveform's animation come from `isPlaying` —
 *     they describe motion
 *   - the position readout, the scrubber, the playhead and the row highlight come from
 *     `isActive` — they describe where you are, which is exactly what a pause preserves
 *
 * Before `paused` existed there was only the one predicate, and gating position on it
 * was right for the wrong reason: pausing cleared `songId`, so there was no state in
 * which a song was your position without also being in motion. Now there is, and
 * collapsing the two would show a paused playlist parked at 0:00 while the store still
 * held 1:20 — the "player state stays synchronized" defect, reintroduced one layer up.
 */
export function usePlayer() {
  const songId = usePlayerStore((s) => s.songId);
  const mediaDurationSec = usePlayerStore((s) => s.mediaDurationSec);
  const paused = usePlayerStore((s) => s.paused);
  const versionId = usePlayerStore((s) => s.versionId);
  const elapsed = usePlayerStore((s) => s.elapsed);
  const loading = usePlayerStore((s) => s.loading);
  const pendingStart = usePlayerStore((s) => s.pendingStart);
  const bufferedSec = usePlayerStore((s) => s.bufferedSec);
  const previewSongId = usePlayerStore((s) => s.previewSongId);
  const previewElapsed = usePlayerStore((s) => s.previewElapsed);
  const previewDurationSec = usePlayerStore((s) => s.previewDurationSec);
  const previewPaused = usePlayerStore((s) => s.previewPaused);
  const backgroundVersionSongId = usePlayerStore((s) => s.backgroundVersionSongId);
  const backgroundVersionId = usePlayerStore((s) => s.backgroundVersionId);
  const backgroundVersionPaused = usePlayerStore((s) => s.backgroundVersionPaused);
  const volume = usePlayerStore((s) => s.volume);
  const speed = usePlayerStore((s) => s.speed);
  const repeat = usePlayerStore((s) => s.repeat);
  const muted = usePlayerStore((s) => s.muted);
  const queue = usePlayerStore((s) => s.queue);
  const toggleRaw = usePlayerStore((s) => s.toggle);
  const playRaw = usePlayerStore((s) => s.play);
  const loadRaw = usePlayerStore((s) => s.load);
  const toggleVersionRaw = usePlayerStore((s) => s.toggleVersion);
  const loadVersionRaw = usePlayerStore((s) => s.loadVersion);
  const playQueueRaw = usePlayerStore((s) => s.playQueue);
  const playNextRaw = usePlayerStore((s) => s.playNext);
  const playPreviousRaw = usePlayerStore((s) => s.playPrevious);
  const pauseOther = usePlayerStore((s) => s.pauseOther);
  const stopRaw = usePlayerStore((s) => s.stop);
  const setElapsed = usePlayerStore((s) => s.setElapsed);
  const setVolume = usePlayerStore((s) => s.setVolume);
  const setSpeed = usePlayerStore((s) => s.setSpeed);
  const toggleRepeat = usePlayerStore((s) => s.toggleRepeat);
  const toggleMute = usePlayerStore((s) => s.toggleMute);
  const songs = useSongsStore((s) => s.songs);
  const openMiniPlayerRaw = useUiStore((s) => s.openMiniPlayer);
  const closeMiniPlayer = useUiStore((s) => s.closeMiniPlayer);

  const previewingSong: Song | null = selectSongById(songs, previewSongId);
  const previewActive = Boolean(previewSongId);
  const backgroundVersionActive = Boolean(backgroundVersionSongId && backgroundVersionId);
  const playingSong: Song | null = selectSongById(songs, songId);
  /*
    The specific version loaded in the player, when one is. Set by toggleVersion/loadVersion
    — a version row's Play button, or the "Load" menu item — and null for ordinary song
    playback, in which case everything below reads exactly as it did before versions.
  */
  const loadedVersion: SongVersion | null =
    playingSong && versionId ? playingSong.versions.find((v) => v.id === versionId) ?? null : null;
  /*
    The version's own length governs when a version is loaded — a version created by a Trim
    ends before the track it came from — otherwise the song's. Real media still wins when it
    reports a concrete duration, the existing rule, since that reflects the actual bytes.
  */
  const declaredDurationSec = loadedVersion
    ? parseLength(loadedVersion.length)
    : playingSong
      ? parseLength(playingSong.length)
      : 0;
  const durationSec = mediaDurationSec && mediaDurationSec > 0 ? mediaDurationSec : declaredDurationSec;
  /** 0–100, clamped. Zero when nothing is playing. */
  const progress = durationSec > 0 ? Math.min(100, (elapsed / durationSec) * 100) : 0;

  /**
   * Starts or pauses playback.
   *
   * A play is counted only after at least 30 seconds have actually been listened to on the
   * current run. Resuming a paused run keeps accumulating toward that same count; starting the
   * track over resets the 30-second requirement for the new run.
   */
  function toggle(targetSongId: string) {
    if (previewSongId === targetSongId && previewActive && activePreviewAudio()) {
      togglePreviewAudio();
      return;
    }
    if (previewActive && activePreviewAudio()) stopPreviewAudio();
    handBackAudioFromBackgroundVersion(targetSongId, null);
    toggleRaw(targetSongId);
  }

  /** Plays a song (or one of its versions) outright. */
  function play(targetSongId: string, versionId: string | null = null) {
    if (previewActive && activePreviewAudio()) stopPreviewAudio();
    handBackAudioFromBackgroundVersion(targetSongId, versionId);
    playRaw(targetSongId, versionId);
  }

  /** Cues a song paused at 0:00 without starting it. */
  function load(targetSongId: string) {
    if (previewActive && activePreviewAudio()) stopPreviewAudio();
    handBackAudioFromBackgroundVersion(targetSongId, null);
    loadRaw(targetSongId);
  }

  /**
   * Toggles the loaded song directly, without routing through Mix preview ownership.
   *
   * Used by the Mix transport when preview audio should stay cached but unfocused.
   */
  function toggleSource(targetSongId: string) {
    handBackAudioFromBackgroundVersion(targetSongId, null);
    toggleRaw(targetSongId);
  }

  /**
   * Plays a specific version, or pauses/resumes it when that version is already loaded.
   *
   * The version-row Play button's action. See playerStore.toggleVersion for the rule: the
   * pause/resume-vs-restart decision is keyed on the (song, version) pair, not the song
   * alone, so switching to another version of the same song restarts rather than resumes.
   */
  function toggleVersion(targetSongId: string, targetVersionId: string) {
    if (previewActive && activePreviewAudio()) stopPreviewAudio();
    handBackAudioFromBackgroundVersion(targetSongId, targetVersionId);
    toggleVersionRaw(targetSongId, targetVersionId);
  }

  /**
   * Cues a version paused at 0:00 — the player half of the "Load" menu item.
   *
   * Retargeting the edit tools onto the version is the caller's other half
   * (useStudio.loadVersionForEditing); this only touches the player, so the transport
   * shows the version's runtime while nothing advances until play is pressed.
   */
  function loadVersion(targetSongId: string, targetVersionId: string) {
    if (previewActive && activePreviewAudio()) stopPreviewAudio();
    handBackAudioFromBackgroundVersion(targetSongId, targetVersionId);
    loadVersionRaw(targetSongId, targetVersionId);
  }

  /** Plays or pauses a Version History audition without retargeting the transport. */
  function toggleBackgroundVersion(targetSongId: string, targetVersionId: string) {
    if (previewActive && activePreviewAudio()) stopPreviewAudio();
    toggleBackgroundVersionAudio(targetSongId, targetVersionId);
  }

  /**
   * Starts a queue.
   *
   * Does NOT record a play — `playQueue` sets `songId` through the store, and the queue's
   * tracks are counted as the clock reaches each of them via `playNext`, so counting here
   * as well would double the first track of every playlist. Kept as a wrapper even though
   * it now adds nothing, because every caller takes it from this hook and moving them all
   * to the raw store action would be a wide change for no gain — and because the mini
   * player rules below need a queue-shaped entry point to build on.
   */
  function playQueue(songIds: string[], startIndex = 0) {
    if (previewActive && activePreviewAudio()) stopPreviewAudio();
    playQueueRaw(songIds, startIndex);
  }

  /**
   * Plays a song IN the mini player: starts it, and opens the bar in one gesture.
   *
   * THE ONE EXPLICIT REQUEST FOR THE BAR from a song surface, and the reason the reveal
   * was taken out of `play` above. A user who wants the floating transport now says so,
   * on the row they want to hear, and everyone who just wants to hear the song gets the
   * row's own controls without a bar appearing over them.
   *
   * IT IS NOT A SECOND PLAYER. It calls the same `play` every other surface calls, on the
   * same store, so the song it starts becomes THE active song — whatever was playing
   * before stops being active, because there is one `songId`. The row it was launched from
   * goes back to showing an inactive row, and the bar and that row are two views of one
   * position. There is nothing here that could play independently: see stores/playerStore.
   *
   * Re-requesting the song that is already active still opens the bar and still calls
   * `play`, which restarts it from zero. That is the honest reading of "play this in the
   * mini player" pressed deliberately from a menu, and it avoids the alternative where the
   * action silently does nothing on the track you are listening to.
   */
  function playInMiniPlayer(targetSongId: string, versionId: string | null = null) {
    play(targetSongId, versionId);
    openMiniPlayerRaw();
  }

  /**
   * Plays a queue in the mini player. The playlist counterpart of the above.
   *
   * Same store, same queue, same clock — a playlist started this way is the same playback
   * session the Playlist screen's own transport shows, and stepping it with the bar's
   * Prev/Next walks the queue that screen set. There is deliberately no separate
   * mini-player queue.
   */
  function playQueueInMiniPlayer(songIds: string[], startIndex = 0) {
    playQueue(songIds, startIndex);
    openMiniPlayerRaw();
  }

  /**
   * Closes the mini player AND stops playback. What the bar's close button does.
   *
   * CLOSE MEANS STOP, and the pairing is the whole function. The bar used to hide while
   * the track carried on counting, which left audio running with no visible owner: the
   * one surface that said what was playing had just been dismissed, so the only way to
   * silence it was to find the song again. "Close the player" is not a request to keep
   * listening invisibly.
   *
   * Both halves, in one place, because either alone is a defect — a stop without the close
   * leaves an empty bar, and a close without the stop is the bug above. `stop()` empties
   * the player (no song, no position, no queue), so nothing is left for the bar to come
   * back for and nothing can be paused into bringing it back.
   */
  function stopAndCloseMiniPlayer() {
    if (previewActive) {
      stopPreviewAudio();
      closeMiniPlayer();
      return;
    }
    stopRaw();
    closeMiniPlayer();
  }

  function playNext() {
    if (previewActive && activePreviewAudio()) stopPreviewAudio();
    playNextRaw();
  }

  function playPrevious() {
    if (previewActive && activePreviewAudio()) stopPreviewAudio();
    playPreviousRaw();
  }

  function stop() {
    if (previewActive && activePreviewAudio()) {
      stopPreviewAudio();
      return;
    }
    if (backgroundVersionActive) {
      handBackAudioFromBackgroundVersion(null, null);
      return;
    }
    stopRaw();
  }

  /**
   * Moves the playback position within a specific song.
   *
   * Takes the song rather than assuming the active one, and that is not incidental:
   * `elapsed` in the store belongs to whatever is playing, so seeking while a
   * *different* track is active would scrub that one. Naming the target lets this
   * make the song active first, which is what someone dragging the bar of a paused
   * track means — and it keeps that ordering inside the hook, where the length is
   * read from the songs list instead of from a `durationSec` that a caller would
   * still be holding at its pre-`load` value.
   *
   * Clamped and rounded here as well as in the seek bar. The bar clamps against the
   * pixels it was dragged over; this clamps against the song, so a keyboard End, a
   * programmatic call or a future deep link cannot put the playhead past the end
   * either. `elapsed` is whole seconds everywhere it is rendered, hence the round.
   */
  function seek(targetSongId: string, seconds: number) {
    if (previewSongId === targetSongId && previewActive && activePreviewAudio()) {
      seekPreviewAudio(seconds);
      return;
    }
    const target = selectSongById(songs, targetSongId);
    if (!target) return;
    // Clamp against the ACTUAL active transport duration when we already know it. A loaded
    // version can carry stale declared metadata — the original in Version History was observed
    // at 0:09 while the real media was much longer — and the scrubber itself already spans the
    // transport's resolved duration. Falling back to the declared version/song length keeps the
    // old behaviour for unloaded targets or before metadata has arrived.
    const activeVersion =
      songId === targetSongId && versionId
        ? target.versions.find((v) => v.id === versionId)
        : null;
    const declaredTotal = parseLength(activeVersion?.length ?? target.length);
    const total = songId === targetSongId && mediaDurationSec && mediaDurationSec > 0 ? mediaDurationSec : declaredTotal;
    if (total <= 0) return;
    if (songId !== targetSongId) load(targetSongId);
    pendingExplicitSeek = { songId: targetSongId, versionId: songId === targetSongId ? versionId : null };
    setElapsed(Math.round(Math.max(0, Math.min(total, seconds))));
  }

  /*
    Where the playhead sits in the queue, derived rather than stored — see the note on
    `queue` in playerStore. Both are -1/false when nothing is queued, which is the
    common case, and that is what makes the queue-aware transport controls disappear
    for single-track playback rather than sitting there disabled.
  */
  const queueIndex = songId ? queue.indexOf(songId) : -1;

  /*
    The queue as songs rather than ids, for the surface that lists it.

    Resolved on read against the library, which is the same rule playlistSongs follows
    and for the same reason: a title edited or a cover replaced mid-playback is already
    reflected, with nothing to synchronise. Unresolvable ids are dropped, so a song
    deleted out from under the queue cannot render an empty row — and `queueIndex` above
    stays correct because the delete path clears the player (stopIfPlaying) and the
    playlist edit path removes the id from `queue` itself (dropFromQueue).
  */
  const queueSongs: Song[] = queue
    .map((id) => selectSongById(songs, id))
    .filter((s): s is Song => s !== null);

  const bufferedSecForSong = playingSong?.audioUrl && songId ? bufferedSec : 0;

  /*
    The position within `queueSongs`, which is NOT always `queueIndex`.

    They differ by however many ids ahead of the playhead failed to resolve, because the
    filter above closes the gaps. `queueIndex` is the right number for stepping the store
    (it indexes `queue`, which is what playNext walks); this is the right number for
    anything that indexes the resolved list. Deriving it separately rather than reusing
    one for both is what stops a list highlighting the wrong row.
  */
  const queueSongIndex = songId ? queueSongs.findIndex((s) => s.id === songId) : -1;

  return {
    songId,
    paused,
    versionId,
    playingSong,
    previewSongId,
    previewingSong,
    previewActive,
    previewPaused,
    previewElapsed,
    previewDurationSec,
    previewDurationLabel:
      previewDurationSec && previewDurationSec > 0 ? formatTime(previewDurationSec) : previewingSong?.length ?? '0:00',
    previewPlaying: previewActive && !previewPaused,
    backgroundVersionSongId,
    backgroundVersionId,
    backgroundVersionPlaying: backgroundVersionActive && !backgroundVersionPaused,
    /** The specific version loaded in the player, or null for ordinary song playback. */
    loadedVersion,
    elapsed,
    loading,
    pendingStart,
    bufferedSec: bufferedSecForSong,
    durationSec,
    durationLabel: durationSec > 0 ? formatTime(durationSec) : playingSong?.length ?? '0:00',
    progress,
    volume,
    speed,
    repeat,
    muted,
    queue,
    /** The queue resolved to songs, for the surface that lists it. */
    queueSongs,
    queueIndex,
    /** The playhead's index within `queueSongs`. See the note above. */
    queueSongIndex,
    /** True when there is a queued song after/before the current one. */
    hasNext: queueIndex >= 0 && queueIndex < queue.length - 1,
    hasPrevious: queueIndex > 0,
    /** True when this song is loaded, running or paused. See the note above. */
    isActive: (id: string) => songId === id,
    /** True when this song is loaded AND advancing. */
    isPlaying: (id: string) => songId === id && !paused,
    /** True when this exact (song, version) is the loaded one, running or paused. */
    isActiveVersion: (id: string, vid: string) => songId === id && versionId === vid,
    /** True when this exact (song, version) is loaded AND advancing. */
    isPlayingVersion: (id: string, vid: string) => songId === id && versionId === vid && !paused,
    toggle,
    play,
    load,
    toggleSource,
    toggleVersion,
    loadVersion,
    toggleBackgroundVersion,
    playQueue,
    /** Starts a song and opens the mini player. See the note above. */
    playInMiniPlayer,
    /** Starts a queue and opens the mini player. */
    playQueueInMiniPlayer,
    /** Closes the mini player and stops the player. What its close button does. */
    stopAndCloseMiniPlayer,
    playNext,
    playPrevious,
    pauseOther,
    stop,
    seek,
    setVolume,
    setSpeed,
    toggleRepeat,
    toggleMute,
  };
}
